import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useMutation } from "react-query";
import { AddUser } from "../../../Services/UserProfile";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Enter valid 10 digit mobile")
    .required("Mobile is required"),
  password: Yup.string()
    .min(6, "Minimum 6 characters")
    .required("Password is required"),
});

const AddUserModal = ({ open, onClose, fetchUserProfile }) => {
  const [showPassword, setShowPassword] = useState(false);

  const { mutate: addUserData } = useMutation(AddUser, {
    onSuccess: (res) => {
      if (res?.data?.response_code === 200) {
        toast.success(res?.data?.message);
        fetchUserProfile();
        formik?.resetForm();

        onClose();
      } else {
        toast.warn(res?.data?.message);
      }
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      password: "",
      role: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      addUserData(values);
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Add User</h2>
          <button
            onClick={onClose}
            className="text-xl text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="px-5 py-4 space-y-4">
          {/* Name */}

          {/* User Type */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              User Type
            </label>

            <select
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`mt-1 w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2
      ${
        formik.touched.role && formik.errors.role
          ? "border-red-500 focus:ring-red-400"
          : "focus:ring-green-500"
      }`}
            >
              <option value="">Select user type</option>
              <option value="Admin">Admin</option>
              <option value="User">User</option>
              <option value="Chat Agent">Chat Agent</option>
            </select>

            {formik.touched.role && formik.errors.role && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.role}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              placeholder="Enter full name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${
                  formik.touched.name && formik.errors.name
                    ? "border-red-500 focus:ring-red-400"
                    : "focus:ring-green-500"
                }`}
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${
                  formik.touched.email && formik.errors.email
                    ? "border-red-500 focus:ring-red-400"
                    : "focus:ring-green-500"
                }`}
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.email}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="text-sm font-medium text-gray-700">Mobile</label>
            <input
              type="tel"
              name="mobile"
              placeholder="10 digit mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${
                  formik.touched.mobile && formik.errors.mobile
                    ? "border-red-500 focus:ring-red-400"
                    : "focus:ring-green-500"
                }`}
            />
            {formik.touched.mobile && formik.errors.mobile && (
              <p className="text-xs text-red-500 mt-1">
                {formik.errors.mobile}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Password
            </label>

            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2
        ${
          formik.touched.password && formik.errors.password
            ? "border-red-500 focus:ring-red-400"
            : "focus:ring-green-500"
        }`}
              />

              {/* Eye Icon */}
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {formik.touched.password && formik.errors.password && (
              <p className="text-xs text-red-500 mt-1">
                {formik.errors.password}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 rounded-lg border text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full py-2.5 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
